'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import TeamLogo from '@/components/ui/TeamLogo';

type ProfileTab = 'profile' | 'preferences' | 'alerts' | 'billing';

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
}

const PLAN_CONFIG = {
  premium: {
    label: 'Premium',
    price: '$49/mois',
    color: 'text-primary',
    bg: 'bg-primary/10 border-primary/30',
    features: ['Projections illimitées', 'Alertes en temps réel', 'Historique complet', 'Analytics avancés', 'Export CSV'],
  },
};

const FAVORITE_TEAMS = [
  { id: 'fav-fla', name: 'Florida Panthers', abbr: 'FLA', color: '#C8102E', active: true },
  { id: 'fav-edm', name: 'Edmonton Oilers', abbr: 'EDM', color: '#FF4C00', active: true },
  { id: 'fav-tor', name: 'Toronto Maple Leafs', abbr: 'TOR', color: '#00205B', active: false },
];

const BILLING_HISTORY = [
  { id: 'bill-001', date: '01 mai 2026', description: 'NHL Analytics Pro — Premium', amount: '$49.00', status: 'Payé' },
  { id: 'bill-002', date: '01 avr 2026', description: 'NHL Analytics Pro — Premium', amount: '$49.00', status: 'Payé' },
  { id: 'bill-003', date: '01 mar 2026', description: 'NHL Analytics Pro — Premium', amount: '$49.00', status: 'Payé' },
  { id: 'bill-004', date: '01 fév 2026', description: 'NHL Analytics Pro — Premium', amount: '$49.00', status: 'Payé' },
];

const ALERT_SETTINGS = [
  { id: 'alert-injuries', label: 'Alertes blessures', description: 'Notification immédiate lors d\'une blessure impactant une projection', enabled: true },
  { id: 'alert-lineup', label: 'Changements alignement', description: 'Gardien de départ confirmé / changements de trio', enabled: true },
  { id: 'alert-line-move', label: 'Mouvement de lignes', description: 'Variation significative des probabilités (>5%)', enabled: false },
  { id: 'alert-b2b', label: 'Back-to-back détecté', description: 'Alerte fatigue calendrier pour vos équipes favorites', enabled: true },
  { id: 'alert-high-conf', label: 'Picks haute confiance', description: 'Notification quand un pick ≥80% est disponible', enabled: true },
];

function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none ${enabled ? 'bg-primary' : 'bg-muted border border-border'}`}
      role="switch"
      aria-checked={enabled}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${enabled ? 'translate-x-4' : 'translate-x-1'}`}
      />
    </button>
  );
}

export default function ProfileClient() {
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [alertSettings, setAlertSettings] = useState(ALERT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [favoriteTeams, setFavoriteTeams] = useState(FAVORITE_TEAMS);

  const { register, handleSubmit, formState: { errors } } = useForm<ProfileForm>({
    defaultValues: {
      firstName: 'Marc',
      lastName: 'Aubert',
      email: 'marc.aubert@nhlpro.ca',
      username: 'maubert_nhl',
    },
  });

  const onSaveProfile = async () => {
    setSaving(true);
    // Backend integration point: PATCH /api/user/profile
    await new Promise((r) => setTimeout(r, 900));
    setSaving(false);
    toast.success('Profil mis à jour avec succès');
  };

  const toggleAlert = (id: string) => {
    setAlertSettings((prev) =>
      prev.map((a) => (a.id === id ? { ...a, enabled: !a.enabled } : a))
    );
    toast.success('Préférence d\'alerte mise à jour');
  };

  const toggleFavorite = (id: string) => {
    setFavoriteTeams((prev) =>
      prev.map((t) => (t.id === id ? { ...t, active: !t.active } : t))
    );
  };

  const TABS: { key: ProfileTab; label: string; icon: string }[] = [
    { key: 'profile', label: 'Profil', icon: 'UserCircleIcon' },
    { key: 'preferences', label: 'Préférences', icon: 'AdjustmentsHorizontalIcon' },
    { key: 'alerts', label: 'Alertes', icon: 'BellIcon' },
    { key: 'billing', label: 'Facturation', icon: 'CreditCardIcon' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-6">
      {/* Left — profile card + nav */}
      <div className="lg:col-span-1 space-y-4">
        {/* Profile header card */}
        <div className="card p-5 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center mx-auto mb-3">
            <span className="text-xl font-bold text-primary font-mono-data">MA</span>
          </div>
          <p className="text-sm font-semibold text-foreground">Marc Aubert</p>
          <p className="text-xs text-muted-foreground">marc.aubert@nhlpro.ca</p>
          <div className="mt-3 flex justify-center">
            <span className={`status-badge border text-xs ${PLAN_CONFIG.premium.bg} ${PLAN_CONFIG.premium.color}`}>
              <Icon name="SparklesIcon" size={11} />
              {PLAN_CONFIG.premium.label}
            </span>
          </div>
          <div className="mt-4 pt-4 border-t border-border space-y-2 text-left">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Membre depuis</span>
              <span className="text-foreground font-mono-data">Oct 2024</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Projections vues</span>
              <span className="text-foreground font-mono-data">1,247</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Alertes reçues</span>
              <span className="text-foreground font-mono-data">342</span>
            </div>
          </div>
        </div>

        {/* Tab nav */}
        <div className="card p-2 space-y-0.5">
          {TABS.map((tab) => (
            <button
              key={`profile-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`nav-item w-full text-left ${activeTab === tab.key ? 'nav-item-active' : ''}`}
            >
              <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Usage meter */}
        <div className="card p-4">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Utilisation ce mois</p>
          <div className="space-y-3">
            {[
              { label: 'Projections', used: 847, total: 999, color: 'bg-primary' },
              { label: 'Alertes', used: 38, total: 50, color: 'bg-accent' },
              { label: 'Exports CSV', used: 4, total: 10, color: 'bg-positive' },
            ].map((meter) => (
              <div key={`meter-${meter.label}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{meter.label}</span>
                  <span className="font-mono-data text-foreground">{meter.used}/{meter.total}</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${meter.color}`}
                    style={{ width: `${(meter.used / meter.total) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — tab content */}
      <div className="lg:col-span-3 space-y-4">
        {/* Profile tab */}
        {activeTab === 'profile' && (
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Informations du compte</h3>
            <form onSubmit={handleSubmit(onSaveProfile)} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="firstName">Prénom</label>
                  <input
                    id="firstName"
                    {...register('firstName', { required: 'Le prénom est requis' })}
                    className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                  />
                  {errors.firstName && <p className="text-xs text-negative mt-1">{errors.firstName.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="lastName">Nom</label>
                  <input
                    id="lastName"
                    {...register('lastName', { required: 'Le nom est requis' })}
                    className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                  />
                  {errors.lastName && <p className="text-xs text-negative mt-1">{errors.lastName.message}</p>}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="email">Adresse email</label>
                <input
                  id="email"
                  type="email"
                  {...register('email', {
                    required: 'L\'email est requis',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Email invalide' },
                  })}
                  className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                />
                {errors.email && <p className="text-xs text-negative mt-1">{errors.email.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="username">Nom d&apos;utilisateur</label>
                <p className="text-2xs text-muted-foreground mb-1.5">Utilisé pour les partages et exports publics</p>
                <input
                  id="username"
                  {...register('username', { required: 'Le nom d\'utilisateur est requis', minLength: { value: 3, message: 'Minimum 3 caractères' } })}
                  className="w-full bg-input border border-border rounded px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors font-mono-data"
                />
                {errors.username && <p className="text-xs text-negative mt-1">{errors.username.message}</p>}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <button type="button" className="btn-ghost text-xs text-muted-foreground">Annuler</button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2 min-w-[120px] justify-center"
                >
                  {saving ? (
                    <>
                      <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Icon name="CheckIcon" size={14} />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Preferences tab */}
        {activeTab === 'preferences' && (
          <div className="space-y-4">
            {/* Plan card */}
            <div className={`card p-5 border ${PLAN_CONFIG.premium.bg}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="SparklesIcon" size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold text-primary">Plan {PLAN_CONFIG.premium.label}</h3>
                  </div>
                  <p className="text-2xl font-bold font-mono-data text-foreground">{PLAN_CONFIG.premium.price}</p>
                  <p className="text-xs text-muted-foreground mt-1">Renouvellement le 1er juin 2026</p>
                </div>
                <button className="btn-secondary text-xs">Gérer l&apos;abonnement</button>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PLAN_CONFIG.premium.features.map((f) => (
                  <div key={`feat-${f}`} className="flex items-center gap-1.5 text-xs text-foreground">
                    <Icon name="CheckCircleIcon" size={12} className="text-positive flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>

            {/* Favorite teams */}
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3">Équipes Favorites</h3>
              <p className="text-xs text-muted-foreground mb-4">Recevez des alertes prioritaires pour ces équipes</p>
              <div className="space-y-2">
                {favoriteTeams.map((team) => (
                  <div key={team.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <TeamLogo abbreviation={team.abbr} color={team.color} size="sm" />
                      <span className="text-sm text-foreground">{team.name}</span>
                    </div>
                    <Toggle enabled={team.active} onChange={() => toggleFavorite(team.id)} />
                  </div>
                ))}
              </div>
              <button className="btn-ghost text-xs text-primary mt-3 flex items-center gap-1.5">
                <Icon name="PlusIcon" size={13} />
                Ajouter une équipe
              </button>
            </div>

            {/* Display preferences */}
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4">Préférences d&apos;affichage</h3>
              <div className="space-y-4">
                {[
                  { id: 'pref-prob', label: 'Afficher les probabilités en %', description: 'Alternative: affichage en cotes décimales', enabled: true },
                  { id: 'pref-xg', label: 'Afficher xGoals par défaut', description: 'Priorise les métriques avancées sur les statistiques brutes', enabled: true },
                  { id: 'pref-auto', label: 'Actualisation automatique', description: 'Rafraîchit les données toutes les 5 minutes', enabled: false },
                ].map((pref) => (
                  <div key={pref.id} className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-foreground">{pref.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{pref.description}</p>
                    </div>
                    <Toggle
                      enabled={pref.enabled}
                      onChange={() => toast.success('Préférence mise à jour')}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Alerts tab */}
        {activeTab === 'alerts' && (
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Préférences d&apos;Alertes</h3>
              <span className="text-xs text-muted-foreground">
                {alertSettings.filter((a) => a.enabled).length}/{alertSettings.length} actives
              </span>
            </div>
            <div className="space-y-4">
              {alertSettings.map((alert) => (
                <div key={alert.id} className="flex items-start justify-between gap-4 pb-4 border-b border-border last:border-0 last:pb-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{alert.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{alert.description}</p>
                  </div>
                  <Toggle enabled={alert.enabled} onChange={() => toggleAlert(alert.id)} />
                </div>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-border">
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Canal de notification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { id: 'chan-app', label: 'In-app', icon: 'BellIcon', active: true },
                  { id: 'chan-email', label: 'Email', icon: 'EnvelopeIcon', active: true },
                  { id: 'chan-sms', label: 'SMS', icon: 'DevicePhoneMobileIcon', active: false },
                ].map((chan) => (
                  <div
                    key={chan.id}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${chan.active ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}
                  >
                    <Icon name={chan.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                    <span className="text-xs font-medium">{chan.label}</span>
                    {chan.active && <Icon name="CheckIcon" size={12} className="ml-auto" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Billing tab */}
        {activeTab === 'billing' && (
          <div className="space-y-4">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4">Méthode de paiement</h3>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-7 rounded bg-muted border border-border flex items-center justify-center">
                    <Icon name="CreditCardIcon" size={16} className="text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-foreground">Visa •••• 4892</p>
                    <p className="text-xs text-muted-foreground">Expire 09/2028</p>
                  </div>
                </div>
                <button className="btn-ghost text-xs">Modifier</button>
              </div>
              <button className="btn-ghost text-xs text-primary mt-2 flex items-center gap-1.5">
                <Icon name="PlusIcon" size={13} />
                Ajouter une méthode de paiement
              </button>
            </div>

            <div className="card overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Historique de facturation</h3>
              </div>
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    <th className="table-header">Date</th>
                    <th className="table-header">Description</th>
                    <th className="table-header">Montant</th>
                    <th className="table-header">Statut</th>
                    <th className="table-header">Reçu</th>
                  </tr>
                </thead>
                <tbody>
                  {BILLING_HISTORY.map((bill) => (
                    <tr key={bill.id} className="border-b border-border row-hover">
                      <td className="table-cell text-xs text-muted-foreground font-mono-data">{bill.date}</td>
                      <td className="table-cell text-sm text-foreground">{bill.description}</td>
                      <td className="table-cell text-sm font-bold font-mono-data text-foreground">{bill.amount}</td>
                      <td className="table-cell">
                        <span className="status-badge bg-positive-subtle text-positive border border-positive/20 text-2xs">{bill.status}</span>
                      </td>
                      <td className="table-cell">
                        <button className="btn-ghost text-xs flex items-center gap-1">
                          <Icon name="ArrowDownTrayIcon" size={12} />
                          PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card p-5 border-negative/20 bg-negative-subtle">
              <h3 className="text-sm font-semibold text-negative mb-2">Zone dangereuse</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Annuler votre abonnement mettra fin à l&apos;accès Premium à la fin de la période de facturation actuelle (31 mai 2026).
              </p>
              <button className="btn-secondary text-xs text-negative border-negative/30 hover:bg-negative-subtle">
                Annuler l&apos;abonnement
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}